import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useMutation } from "@apollo/client";
import { CreateViewMutation } from "core/mutations";
import { createEvent } from "core/utils/tracker";
import getClientIp from "core/utils/getClientIp";
import { getUtmFromLocalStorage } from "core/utils/utm";
import ReactGA from "react-ga4";
import apm from "core/utils/apm";
import ShortyVideo from "./ShortyVideo";
import ShortyVideoContent from "./ShortyVideoContent";

const ShortyPlayer = React.forwardRef(function ShortyPlayer(
  {
    content,
    active,
    preload = false, // Режим предзагрузки (неактивный элемент)
    mainVideoElement = null, // Главный video элемент для активного элемента
    onActiveVideoReady = null, // Callback для передачи данных активного видео в ShortyVirtualFeed
    closeRouteDialogButton = null,
    onPrevious = null,
    onNext = null,
    canGoPrevious = false,
    canGoNext = false,
  },
  ref // eslint-disable-line no-unused-vars
) {
  const [videoData, setVideoData] = useState(null); // API данные
  const innerVideoRef = useRef(null); // Ref для внутреннего video элемента

  // Мемоизированные стабильные функции для ShortyVideo (должны быть в начале!)
  const handleVideoReady = useCallback(({ hlsInstance, hlsUrl }) => {
    console.log("ShortyVideo ready:", { hlsInstance, hlsUrl });
  }, []);

  const handleVideoError = useCallback((error) => {
    console.error("ShortyVideo error:", error);
  }, []);

  const refetchQueries = [];
  if (content?.premium) {
    refetchQueries.push("Viewer");
  }

  const [createView, createViewResult] = useMutation(CreateViewMutation, {
    refetchQueries,
    onCompleted() {
      createEvent(content.type.toLowerCase(), "start");
      ReactGA.event({
        category: "User",
        action: "Create view",
        label: content.title,
      });
    },
  });

  // Создаем просмотр для активных и preload элементов
  useEffect(() => {
    if (!active && !preload) return;

    if (
      content?.access?.token &&
      !createViewResult.data &&
      !createViewResult.loading
    ) {
      const variables = {
        token: content.access.token,
        utm: getUtmFromLocalStorage(),
      };

      getClientIp(content.getIpUrl)
        .then((ip) => {
          if (ip) {
            variables.clientIp = ip;
          }

          return createView({
            variables,
          });
        })
        .catch((error) => {
          apm.captureError(error);
          console.error("create view error", error);
        });
    }
  }, [active, preload, content, createView, createViewResult.data, createViewResult.loading]);

  const view = createViewResult?.data?.createView;
  const video = view?.video;

  // Сохраняем видео данные при получении ответа от API
  useEffect(() => {
    if (video?.playlist && view?.id) {
      const newVideoData = {
        hlsUrl: video.playlist,
        videoId: video.id,
        viewId: view.id,
        content: content,
        drm: video?.drmPlayerSettings,
      };

      setVideoData(newVideoData);

      // Передаем данные в ShortyVirtualFeed для активного видео
      if (active && onActiveVideoReady) {
        onActiveVideoReady(newVideoData);
      }
    }
  }, [video?.playlist, video?.id, view?.id, content, active, onActiveVideoReady]);

  // Показываем пустой контейнер если нет контента
  if (!content) {
    return null;
  }

  // Если нет HLS URL, показываем только thumbnail
  if (!videoData?.hlsUrl) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: content.previewUrl
            ? `url(${content.previewUrl})`
            : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#000",
          position: "relative",
        }}
      >
        {/* Overlay для затемнения фона */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: active
              ? "rgba(0, 0, 0, 0.1)"
              : "rgba(0, 0, 0, 0.3)",
            zIndex: 1,
          }}
        />

        {/* Контент поверх фона */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            color: "white",
          }}
        >
          {createViewResult.loading && <CircularProgress color="inherit" />}
        </Box>
      </Box>
    );
  }

  // Рендерим ShortyVideo с HLS
  return (
    <ShortyVideo
      hlsUrl={videoData.hlsUrl}
      innerVideoRef={innerVideoRef} // Передаем ref для внутреннего video
      mainVideoElement={active ? mainVideoElement : null} // Только для активных
      onReady={handleVideoReady}
      onError={handleVideoError}
    >
      {(innerVideoRef) => (
        <ShortyVideoContent
          innerVideoRef={innerVideoRef}
          content={content}
          closeRouteDialogButton={closeRouteDialogButton}
          showThumbnail={!active || preload} // Thumbnail для неактивных или preload
          onPrevious={onPrevious}
          onNext={onNext}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          preload={preload}
          active={active}
        />
      )}
    </ShortyVideo>
  );
});

export default ShortyPlayer;

ShortyPlayer.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    previewUrl: PropTypes.string,
    premium: PropTypes.bool,
    access: PropTypes.shape({
      token: PropTypes.string,
    }),
    getIpUrl: PropTypes.string,
  }).isRequired,
  active: PropTypes.bool.isRequired,
  preload: PropTypes.bool,
  mainVideoElement: PropTypes.object,
  onActiveVideoReady: PropTypes.func,
  closeRouteDialogButton: PropTypes.node,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  canGoPrevious: PropTypes.bool,
  canGoNext: PropTypes.bool,
};
